/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import moment from 'moment';
// components
import useAuth from 'src/hooks/useAuth';
import OverviewTab from 'src/sections/@dashboard/lead/OverviewTab';
import AttendedChecklistV2, { useAttendedChecklistVisibility } from 'src/sections/@dashboard/leadDetail/AttendedChecklistV2';
import NotesTab from 'src/sections/@dashboard/lead/NotesTab';
import CallLogs from 'src/sections/@dashboard/leadDetail/CallLogs';
import IosEmailComposerBody from 'src/sections/tasks/composers/IosEmailComposerBody';
import FollowUpWhatsappChat from 'src/components/whatsappComponents/FollowUpWhatsappChat';
import { callApi } from 'src/utils/apiCall';
import { getChoicesList } from 'src/redux/slices/practiceTreatment';
import useGuidedChecklist from 'src/sections/@dashboard/leadDetail/AttendedGuidedChecklist/hooks/useGuidedChecklist';
import { ConsultationAttendedAppointment } from '../Attended';
import { clearLead, getLeadNotes, getLeadReminderData, followUpSlice } from '../../redux/slices/lead';
import { setCurrentTreatmentDataGlobal } from '../../redux/slices/multiTreatment';
import { useSelector, dispatch } from '../../redux/store';
import {
    selectLeadFlowReminder,
    selectLeadNotes,
    selectLeadNotesArr,
} from '../../redux/selectors/leadSelectors';
import AttendedFullscreenWrapper from './AttendedFullscreenWrapper';

export default function OpenConsultationPopup({ selectedTreatmentData, handleReloadLeads, open, onClose, type = 'lead', user, inputRef, headerButtons, startTreatment, OpenAssignTask, editDetailHandler, handleCloseAndReload, defaultTab, initialSidebarTab, onSendComplete }) {
    // Component remounts each time it opens (rendered only when openConsultationAttend
    // is true in NewLead), so initialising from the prop is safe here.
    const [sidebarTab, setSidebarTab] = useState(initialSidebarTab || 'overview');
    // Bumps each time the sidebar is opened programmatically (e.g. follow-up
    // Email/WhatsApp redirect) so the glow overlay re-mounts and replays its
    // attention pulse — draws the user's eye to the composer they need to use.
    const [sidebarPulse, setSidebarPulse] = useState(0);
    // ETM: per-practice email template to preload in the sidebar composer.
    const [sidebarEmailTemplateId, setSidebarEmailTemplateId] = useState(null);
    const [treatmentData, setTreatmentData] = useState(null);
    const [notesLength, setNotesLength] = useState([]);
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const leadFlowReminder = useSelector(selectLeadFlowReminder);
    const leadNotes = useSelector(selectLeadNotes);
    const leadNotesArr = useSelector(selectLeadNotesArr);
    const followLead = useSelector((state) => state.lead.followLead);
    const { user: authUser } = useAuth();
    const isSuperuser = !!authUser?.is_superuser;

    const currentLeadId = user?.lead_id || user?.id;
    const sidebarLeadDetail = followLead || (user ? { ...user, id: currentLeadId } : null);
    const leadDetailForChecklist = (followLead && Number(followLead.id) === Number(currentLeadId))
        ? followLead
        : { id: currentLeadId, lead_treatments: user?.lead_treatments || [] };
    const attendedVisibility = useAttendedChecklistVisibility({
        practiceId,
        leadId: currentLeadId,
        leadDetail: leadDetailForChecklist,
    });

    // Change 5: checklist progress for the header badge.
    // useGuidedChecklist uses a module-level shared store keyed by practiceId:leadId
    // so this subscription is free — it reuses the same fetch as AttendedGuidedChecklistV2.
    const { groups: checklistGroups, allDoneCount } = useGuidedChecklist({
        practiceId,
        leadId: currentLeadId,
        leadDetail: leadDetailForChecklist,
    });
    const checklistDoneRaw  = (checklistGroups?.done?.length ?? 0);
    const checklistActiveRaw = (checklistGroups?.today?.length ?? 0) + (checklistGroups?.later?.length ?? 0);
    // allDoneCount > 0 means the BE confirmed all-done but returned no items — treat as all done.
    const checklistTotal = allDoneCount > 0 ? allDoneCount : (checklistDoneRaw + checklistActiveRaw);
    const checklistDone  = allDoneCount > 0 ? allDoneCount : checklistDoneRaw;

    useEffect(() => {
        const currentLeadId = user?.lead_id || user?.id;
        if (!practiceId || !currentLeadId) return;
        setNotesLength([]);
        dispatch(getLeadNotes(currentLeadId, practiceId));
        dispatch(getLeadReminderData(practiceId, 'LeadDetail', 10, 0, 'leadNotes', currentLeadId, '', '', '', 'counter', '', false, true));
        dispatch(getChoicesList(practiceId));
        dispatch(followUpSlice(currentLeadId, practiceId, 'LeadDetail'));
    }, [user?.id, user?.lead_id, practiceId]);

    useEffect(() => {
        const fetchData = async () => {
            const currentLeadId = user?.lead_id || user?.id;
            if (!practiceId || !currentLeadId) return;
            try {
                const resp = await callApi(`/api/v1/practices/${practiceId}/leads/${currentLeadId}/treatments`, 'GET');
                setTreatmentData(resp?.data?.results ?? []);
            } catch (error) {
                console.error('Error fetching treatment data:', error);
            }
        };
        fetchData();
    }, [practiceId, user?.id, user?.lead_id]);

    useEffect(() => {
        const combineNotes = [];
        if (leadNotes?.length > 0) {
            combineNotes.push(...leadNotes.filter((el) => el.additional_notes !== ''));
        }
        if (leadNotesArr?.results?.length > 0) {
            combineNotes.push(...leadNotesArr.results.filter((el) => el.note !== ''));
        }
        if (combineNotes.length) {
            setNotesLength(
                combineNotes.sort(
                    (a, b) =>
                        new Date(moment(a.updated_at, 'DD-MM-YYYY HH:mm:ss')) -
                        new Date(moment(b.updated_at, 'DD-MM-YYYY HH:mm:ss'))
                )
            );
        }
    }, [user, leadNotes, leadNotesArr]);

    // Reset pulse counter when the popup closes
    useEffect(() => {
        if (!open) setSidebarPulse(0);
    }, [open]);

    const handleTabChange = (event, newValue) => setSidebarTab(newValue);

    // Programmatic tab open — bumps pulse so the glow ring replays
    const openSidebarTab = (v, opts) => {
        setSidebarTab(v);
        if (v === 'email') setSidebarEmailTemplateId(opts?.templateId ?? null);
        setSidebarPulse((n) => n + 1);
    };

    const handleClose = async (action) => {
        if (action === 'tco') {
            return;
        }
        dispatch(clearLead());
        dispatch(setCurrentTreatmentDataGlobal(null));
        onClose();
    };

    const SIDEBAR_TABS = [
        {
            icon: 'grommet-icons:overview',
            label: 'Overview',
            value: 'overview',
            component: (
                <OverviewTab
                    treatmentTabData={treatmentData || []}
                    leadId={user?.lead_id || user?.id}
                    setExpandLayout={() => {}}
                    handleChange={handleTabChange}
                    expandLayout={false}
                    leadReminder={leadFlowReminder}
                    user={user}
                    LeadNotes={notesLength}
                    showDetailModes
                />
            ),
            width: 24,
            height: 24,
        },
        ...(attendedVisibility.visible ? [{
            icon: 'mdi:checkbox-marked-outline',
            label: 'Attended Checklist',
            value: 'checklist',
            component: (
                <AttendedChecklistV2
                    practiceId={practiceId}
                    leadId={currentLeadId}
                    leadDetail={leadDetailForChecklist}
                />
            ),
            width: 20,
            height: 20,
        }] : []),
        {
            icon: 'solar:chat-round-dots-bold',
            label: 'Notes',
            value: 'notes',
            component: (
                <NotesTab
                    LeadNotes={notesLength}
                    isForTab
                    userDetails={user}
                    handleChange={handleTabChange}
                />
            ),
            width: 20,
            height: 20,
        },
        {
            icon: 'solar:phone-calling-rounded-bold',
            label: 'Calls',
            value: 'calls',
            component: (
                <CallLogs leadDetail={sidebarLeadDetail} />
            ),
            width: 20,
            height: 20,
        },
        {
            icon: 'mdi:email-outline',
            label: 'Email',
            value: 'email',
            component: (
                <IosEmailComposerBody
                    leadId={currentLeadId}
                    practiceId={practiceId}
                    leadDetail={followLead || user}
                    templateId={sidebarEmailTemplateId}
                    compact
                    onSendSuccess={onSendComplete}
                />
            ),
            width: 20,
            height: 20,
        },
        {
            icon: 'ic:baseline-whatsapp',
            label: 'WhatsApp',
            value: 'whatsapp',
            component: (
                <FollowUpWhatsappChat
                    user={followLead || user}
                    followLead={followLead}
                    onMessageConfirmed={onSendComplete}
                />
            ),
            width: 20,
            height: 20,
        },
        ...(isSuperuser ? [{
            icon: 'solar:bug-bold-duotone',
            label: 'Debug',
            value: 'debug',
            component: (
                <Box sx={{ p: 2 }}>
                    <Typography sx={{ fontSize: '11px', color: '#6e6e73', letterSpacing: '0.02em', textTransform: 'uppercase', fontWeight: 600, mb: 1.5 }}>
                        V2 Visibility (superadmin)
                    </Typography>
                    <Box sx={{ fontFamily: 'monospace', fontSize: '11px', color: '#1d1d1f', bgcolor: '#F5F5F7', p: 1.5, borderRadius: '8px', lineHeight: 1.7 }}>
                        <div>practiceId: {String(practiceId)}</div>
                        <div>leadId: {String(currentLeadId)}</div>
                        <div>ready: {String(attendedVisibility?.ready)}</div>
                        <div>leadInAttended: {String(attendedVisibility?.leadInAttended)}</div>
                        <div>hasAttendedTask: {String(attendedVisibility?.hasAttendedTask)}</div>
                        <div>visible: {String(attendedVisibility?.visible)}</div>
                        <Box sx={{ height: '1px', bgcolor: 'rgba(0,0,0,0.10)', my: 1 }} />
                        <div>featureAccess: {String(attendedVisibility?.debug?.featureAccess)}</div>
                        <div>groupsCount: {String(attendedVisibility?.debug?.groupsCount ?? '—')}</div>
                        <div>templatesCount: {String(attendedVisibility?.debug?.templatesCount ?? '—')}</div>
                        <div>attendedTemplatesCount: {String(attendedVisibility?.debug?.attendedTemplatesCount ?? '—')}</div>
                        <div>matchingGroupsCount: {String(attendedVisibility?.debug?.matchingGroupsCount ?? '—')}</div>
                        <div>groupsWithItemsCount: {String(attendedVisibility?.debug?.groupsWithItemsCount ?? '—')}</div>
                        {attendedVisibility?.debug?.error && (
                            <div style={{ color: '#FF5630', marginTop: 4 }}>error: {String(attendedVisibility.debug.error)}</div>
                        )}
                        {attendedVisibility?.debug?.sampleTask && (
                            <>
                                <Box sx={{ height: '1px', bgcolor: 'rgba(0,0,0,0.10)', my: 1 }} />
                                <div>sampleTask.id: {String(attendedVisibility.debug.sampleTask.id)}</div>
                                <div>sampleTask.title: {String(attendedVisibility.debug.sampleTask.title)}</div>
                                <div>sampleTask.keys: [{(attendedVisibility.debug.sampleTask.keys || []).join(', ')}]</div>
                                <div>sampleTask.checklist_items.length: {String(attendedVisibility.debug.sampleTask.checklist_items_len)}</div>
                            </>
                        )}
                    </Box>
                </Box>
            ),
            width: 18,
            height: 18,
        }] : []),
    ];

    // Appointment filter: consultation popup matches the specific treatment's appointment,
    // falling back to any ATTENDED appointment
    const appointmentFilter = (appts) => {
        if (selectedTreatmentData?.treatmentData?.id) {
            const specific = appts.filter((e) => e.lead_treatment === selectedTreatmentData.treatmentData.id && e.status === 'ATTENDED');
            if (specific.length) return specific;
        }
        return appts.filter((el) => el.status === 'ATTENDED');
    };

    return (
        <AttendedFullscreenWrapper
            open={open}
            onClose={handleClose}
            type={type}
            headerButtons={headerButtons}
            headerTitle={
                <>
                    Attended consultation for{' '}
                    <span style={{ textTransform: 'capitalize' }}>{user?.first_name}</span>
                </>
            }
            followLead={followLead}
            headerCountStatus="CONSULTATION_ATTENDED"
            appointmentFilter={appointmentFilter}
            sidebarTabs={SIDEBAR_TABS}
            sidebarTab={sidebarTab}
            onTabChange={handleTabChange}
            sidebarPulseKey={sidebarPulse}
            pulseCssName="consultPulse"
            checklistDone={checklistDone}
            checklistTotal={checklistTotal}
        >
            <ConsultationAttendedAppointment
                // Remount per lead so all useState/useRef inside (activeTab,
                // taskActivityDone, defaultedToReminderRef, etc.) reset when a
                // different lead opens — this Dialog is persistent and never
                // unmounts between leads. Key is stable per lead (only changes when
                // the lead changes), so there is no remount loop.
                key={user?.lead_id || user?.id}
                selectedTreatmentData={selectedTreatmentData}
                handleReloadLeads={handleReloadLeads}
                handleCloseAndReload={handleCloseAndReload}
                handleClose={handleClose}
                user={user}
                type='Starting treatment'
                inputRef={inputRef}
                startTreatment={startTreatment}
                OpenAssignTask={OpenAssignTask}
                editDetailHandler={editDetailHandler}
                defaultTab={defaultTab}
                onOpenSidebarTab={openSidebarTab}
            />
        </AttendedFullscreenWrapper>
    );
}

OpenConsultationPopup.propTypes = {
    handleReloadLeads: PropTypes.func,
    open: PropTypes.bool,
    onClose: PropTypes.func,
    type: PropTypes.string,
    user: PropTypes.object,
    inputRef: PropTypes.any,
    headerButtons: PropTypes.bool,
    startTreatment: PropTypes.string,
    OpenAssignTask: PropTypes.any,
    editDetailHandler: PropTypes.any,
    handleCloseAndReload: PropTypes.func,
    selectedTreatmentData: PropTypes.any,
    defaultTab: PropTypes.string,
    initialSidebarTab: PropTypes.string,
    onSendComplete: PropTypes.func,
};
